import { useRef, useState } from 'react';
import { endSession, fetchSessionSnapshot, rememberActiveSession } from './lib/session-core';
import { t } from './lib/i18n';

export function EndGameControl({ sessionId, initData, userId, onEnded }: {
  sessionId: string;
  initData: string;
  userId: string;
  onEnded: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const actionId = useRef<string | null>(null);

  const close = (): void => {
    if (busy) return;
    setConfirming(false);
    setError(null);
    actionId.current = null;
  };

  const finish = (): void => {
    rememberActiveSession(null);
    actionId.current = null;
    setConfirming(false);
    onEnded();
  };

  const end = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const stableActionId = actionId.current ?? crypto.randomUUID();
    actionId.current = stableActionId;
    try {
      await endSession(sessionId, initData, stableActionId);
      finish();
    } catch {
      try {
        const snapshot = await fetchSessionSnapshot(sessionId, initData);
        if (snapshot.players.some((player) => player.userId === userId && player.explicitlyLeft)) {
          finish();
          return;
        }
      } catch {
        // The original end action remains safe to retry with the same idempotency key.
      }
      setError(t("Couldn't confirm that the game ended yet."));
    } finally {
      setBusy(false);
    }
  };

  return <>
    <button className="end-game-trigger" type="button" onClick={() => setConfirming(true)}>{t('END GAME')}</button>
    {confirming ? <div className="end-game-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <section className="end-game-sheet" role="dialog" aria-modal="true" aria-labelledby="end-game-title">
        <p className="eyebrow">{t('LEAVE SESSION')}</p>
        <h2 id="end-game-title">{t('End this game?')}</h2>
        <p>{t('This ends it for everyone in this session.')}</p>
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
        <div className="end-game-actions">
          <button className="secondary-wide" type="button" disabled={busy} onClick={close}>{t('KEEP PLAYING')}</button>
          <button className="end-game-confirm" type="button" disabled={busy} onClick={() => void end()}>{busy ? t('ENDING…') : error ? t('TRY AGAIN') : t('END GAME')}</button>
        </div>
      </section>
    </div> : null}
  </>;
}
